import { useState } from 'react'
import { hasErrors, validateLoginForm } from '../utils/validation'
import { LoginFormErrors, LoginFormState, LoginPageProps } from '../types'
import railsPostFetch, { parseRailsJsonResponse } from '@aytm/lib/common/rails-post-fetch'
import { redirectToHost } from '@aytm/app/users/common/redirect'

const INITIAL_ERRORS: LoginFormErrors = { email: null, password: null, base: null }

type CaptchaState = {
  displayCaptcha: boolean
  turnstileSiteKey: string | null
}

export function useLoginForm(
  props: Pick<LoginPageProps, 'sessionPath' | 'allowedRedirectHosts'> & Partial<CaptchaState>
) {
  const [form, setForm] = useState<LoginFormState>({
    email: '',
    password: '',
    // Opt-in by default (unchecked) — no persistent 30-day auth_token cookie unless the
    // user explicitly chooses it. Matches the Figma mockups and the safer default (GAP-015).
    rememberMe: false,
    turnstileToken: null
  })
  const [errors, setErrors] = useState<LoginFormErrors>(INITIAL_ERRORS)
  const [loading, setLoading] = useState(false)
  const [captcha, setCaptcha] = useState<CaptchaState>({
    displayCaptcha: props.displayCaptcha ?? false,
    turnstileSiteKey: props.turnstileSiteKey ?? null
  })
  // Bumped whenever the spent token is dropped, so the widget mints a fresh one.
  const [turnstileResetSignal, setTurnstileResetSignal] = useState(0)

  function setField<K extends keyof LoginFormState>(key: K, value: LoginFormState[K]) {
    setForm(prev => ({ ...prev, [key]: value }))
    setErrors(prev => ({ ...prev, [key]: null, base: null }))
  }

  // The captcha token arrives from the widget, not from the visitor. Routing it through
  // `setField` cleared the very error that asked for the captcha in the first place.
  function setTurnstileToken(token: string | null) {
    setForm(prev => ({ ...prev, turnstileToken: token }))
  }

  async function handleSubmit(e: React.FormEvent, options: { ssoActive?: boolean } = {}) {
    e.preventDefault()

    // Enter submits straight from the field, so the blur handler never ran and the email
    // may still carry the spaces it would have removed. The password is left untouched —
    // whitespace is significant there (see the validator).
    const normalized = { ...form, email: form.email.trim() }
    if (normalized.email !== form.email) setForm(normalized)

    // Returned so the caller can move focus on every submit attempt. A reactive effect
    // keyed on `errors` re-fired on each keystroke (setField makes a fresh object) and
    // stole focus back to whichever other field still held an error — same bug the
    // signup form already fixed by focusing imperatively from here.
    const validationErrors = validateLoginForm(normalized, { skipPassword: options.ssoActive })
    if (hasErrors(validationErrors)) {
      setErrors(validationErrors)
      return validationErrors
    }

    setLoading(true)
    setErrors(INITIAL_ERRORS)

    try {
      const payload: Record<string, string> = {
        email: normalized.email,
        password: form.password,
        remember_me: form.rememberMe ? '1' : '0'
      }
      if (form.turnstileToken) payload['cf-turnstile-response'] = form.turnstileToken

      const response = await railsPostFetch(props.sessionPath, { body: JSON.stringify(payload) })

      // Shared helper intercepts `{ csrf_failure: true }` / 401 (stale CSRF —
      // session rotated in another tab, deploy bounced, idle session reset)
      // and reloads to pick up a fresh token from the server-rendered <meta>.
      // The `null` return is our signal to abort the rest of this submit
      // (GAP-034 / OQ-LOG-34, applied uniformly across auth forms via OQ-PWD-7).
      const data = await parseRailsJsonResponse(response)
      if (!data) return

      // SSO short-circuit: the server hands back the IdP authorization URL instead of a 302
      // (GAP-024). It is a trusted absolute URL from the in-code provider registry, so navigate
      // the top-level window directly — redirectTo() would reject the absolute URL.
      if (data.sso_redirect_to) {
        window.location.href = data.sso_redirect_to
        return
      }

      if (data.logged_in) {
        // Cross-domain bounce: the server returns an absolute URL on the user's team
        // subdomain (sessions §5.1 — `http://acme.aytm.com/dashboard?ptoken=...`). Absolute
        // destinations are allowed only onto a host the server named at render time, so a
        // destination pointing off the platform lands on the site root instead.
        redirectToHost(data.redirect_to ?? '/', props.allowedRedirectHosts ?? [])
      } else {
        if (data.display_captcha !== undefined) {
          setCaptcha(prev => ({
            displayCaptcha: data.display_captcha ?? prev.displayCaptcha,
            turnstileSiteKey: data.turnstile_site_key ?? prev.turnstileSiteKey
          }))
          setForm(prev => ({ ...prev, turnstileToken: null }))
          setTurnstileResetSignal(n => n + 1)
        }
        // the server's own reason (SSO unavailable, throttled) beats the credential default
        const failure = { ...INITIAL_ERRORS, base: data.error ?? 'Invalid email or password' }
        setErrors(failure)
        return failure
      }
    } catch {
      const failure = { ...INITIAL_ERRORS, base: 'Unexpected error occurred, please try again later' }
      setErrors(failure)
      return failure
    } finally {
      setLoading(false)
    }
  }

  return { form, setField, setTurnstileToken, errors, loading, handleSubmit, captcha, turnstileResetSignal }
}
